/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import type { DatePickerState } from './DatePicker.types';
import type { FlexDateFilters } from './FlexDatePicker.types';
import {
    externalToInternalState,
    getCloseAction,
    getHasSetDates,
    internalToExternalState,
    useGetTriggerContent,
} from './DatePicker.utils';

interface UseDatePickerDraftOptions {
    filters: FlexDateFilters;
    setFilters: (value: FlexDateFilters) => void;
    isFlexibleAllowed: boolean;
}

/**
 * Open state and draft selection shared by every date picker wrapper.
 * The draft resets from the committed filters on open and commits on close.
 */
export function useDatePickerDraft({
    filters,
    setFilters,
    isFlexibleAllowed,
}: UseDatePickerDraftOptions) {
    const [isOpen, setIsOpen] = React.useState(false);

    const [state, setState] = React.useState<DatePickerState>(() =>
        externalToInternalState(filters, isFlexibleAllowed)
    );

    // While closed, show the committed filters rather than the stale draft.
    const visibleState = isOpen
        ? state
        : externalToInternalState(filters, isFlexibleAllowed);
    const triggerContent = useGetTriggerContent(visibleState);
    const drawerTitleContent = useGetTriggerContent(visibleState, true);
    const { hasDates, currentTabHasDates } = getHasSetDates(visibleState);
    // What closing would do to the committed filters. The baseline is the
    // untouched draft's commit, so normalizing stale filters on open doesn't count.
    const closeAction = getCloseAction(
        internalToExternalState(visibleState),
        internalToExternalState(externalToInternalState(filters, isFlexibleAllowed))
    );

    const resetInternalState = () => {
        setState(externalToInternalState(filters, isFlexibleAllowed));
    };

    const onOpenChange = (open: boolean) => {
        setIsOpen(open);

        // Opening resets the draft from the committed filters. That reset is
        // async, so committing here would write the previous draft back out.
        if (open) {
            resetInternalState();
            return;
        }

        // Closing commits the draft. Clearing still commits immediately.
        setFilters(internalToExternalState(state));
    };

    const onClearDates = React.useCallback(
        ({ commit = true }: { commit?: boolean } = {}) => {
            setState((prev) => {
                return {
                    ...prev,
                    startDate: null,
                    endDate: null,
                    availMonths: null,
                    availNights: null,
                    flexDays: null,
                };
            });

            if (commit) {
                setFilters({
                    startDate: null,
                    endDate: null,
                    flexStartRange: null,
                    flexEndRange: null,
                    availMonths: null,
                    availNights: null,
                });
            }
        },
        [setState, setFilters]
    );

    return {
        isOpen,
        onOpenChange,
        state,
        setState,
        onClearDates,
        triggerContent,
        drawerTitleContent,
        hasDates,
        closeAction,
        currentTabHasDates,
    };
}

export type DatePickerDraft = ReturnType<typeof useDatePickerDraft>;
